import { IStudentQuestion } from '@sdk/contracts';
import { Button, Radio } from 'antd';
import React, { useMemo } from 'react';

import { AnswerItem } from './AnswerItem';
import {
  AnswersWrapper,
  FooterWrapper,
  FormulaWrapper,
  IndexAnswer,
  QuestionTitle,
  SectionWrapper,
  StyledImage,
  TitleWrapper,
  Wrapper
} from './QuestionItem.styles';

import { Icon } from '@/components/Icon';
import { Box } from '@/components/Utils';
import {
  useIsCorrectInstantlyActive,
  useQuizSetQuestionIndex,
  useQuizUpdateResponse,
  useResponseForQuestion,
  useReportQuestionForm
} from '@/hooks';

type Props = {
  index: number;
  studentQuestion: IStudentQuestion;
};

const IMAGE_HEIGHT = 150;

const QuestionItemComponent: React.FC<Props> = ({ index, studentQuestion }) => {
  const { setQuestion } = useReportQuestionForm();
  const isCorrectInstantlyActive = useIsCorrectInstantlyActive();
  const setQuestionIndex = useQuizSetQuestionIndex();
  const updateResponse = useQuizUpdateResponse();

  const { question, quiz } = studentQuestion;
  const currentResponse = useResponseForQuestion(question._id);

  const showAnswer = useMemo(() => {
    return currentResponse !== undefined && isCorrectInstantlyActive;
  }, [currentResponse, isCorrectInstantlyActive]);

  const handleClick = (answerIndex: number) => {
    if (showAnswer) return;
    updateResponse(question._id, answerIndex);
    setQuestionIndex(index);
  };

  return (
    <Wrapper>
      <Box gap={16} width="100%">
        <IndexAnswer>{index + 1}</IndexAnswer>
        <TitleWrapper>
          <QuestionTitle>{question.title}</QuestionTitle>
          {question.formula && (
            <FormulaWrapper dangerouslySetInnerHTML={{ __html: question.formula }} />
          )}
          {question.image && (
            <StyledImage
              src={question.image.url}
              height={IMAGE_HEIGHT}
              alt={question.image.name}
              loading="lazy"
            />
          )}
        </TitleWrapper>
      </Box>
      <AnswersWrapper>
        <Radio.Group value={currentResponse} onChange={e => handleClick(e.target.value)}>
          {question.answers.map((answer, answerIndex) => (
            <AnswerItem
              key={`${question._id}-${answerIndex}`}
              answer={answer}
              index={answerIndex}
              question={question}
              showAnswer={showAnswer}
            />
          ))}
        </Radio.Group>
      </AnswersWrapper>
      <FooterWrapper>
        <SectionWrapper>{quiz.section?.name}</SectionWrapper>
        <Button
          type="text"
          size="small"
          iconPosition="end"
          icon={<Icon i="Flag" />}
          onClick={() => setQuestion(question)}
        />
      </FooterWrapper>
    </Wrapper>
  );
};

export const QuestionItem = React.memo(QuestionItemComponent);
